import React, { FC } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Dialog } from "@natera/material/lib/dialog";
import { Button, Form } from "@natera/form";
import { PhoneField } from "@app/components";
import {
  useErrorController,
  usePhoneValidation,
} from "@natera/platform/lib/hooks";
import { getPhoneValidationErrorMessage } from "@app/utils/getPhoneValidationErrorMessage";

import { UserContext } from "@app/provider";
import "./updateMobileNumberDialog.scss";

const messages = defineMessages({
  updateMobileNumberDialogTitle: {
    id: "updateMobileNumberDialogTitle",
    defaultMessage: "Update Mobile Number",
  },
  updateMobileNumberDialogSaveButton: {
    id: "updateMobileNumberDialogSaveButton",
    defaultMessage: "Save",
  },
  updateMobileNumberDialogCancelButton: {
    id: "updateMobileNumberDialogCancelButton",
    defaultMessage: "Cancel",
  },
});

interface Props {
  currentPhone: string;
  handleSave: (newPhoneNumber: string) => Promise<void>;
  handleBack: () => void;
  pageName?: string;
}

const UpdateMobileNumberDialog: FC<Props> = ({
  currentPhone,
  handleSave,
  handleBack,
  pageName,
}) => {
  const intl = useIntl();

  const { clearErrors, getValidationError, setValidationError } =
    useErrorController();
  const phoneValidation = usePhoneValidation();
  const { validatePhoneNumber: validateFromPS } = React.useContext(UserContext);

  const validatePhone = async (mobileNumber: string) => {
    const errorMessage = await getPhoneValidationErrorMessage({
      isMandatory: true,
      mobileNumber,
      intl,
      phoneValidation,
      validateFromPS,
      pageName: pageName,
    });
    if (errorMessage) {
      setValidationError("mobileNumber", errorMessage);
      return false;
    }

    return true;
  };

  const handleSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearErrors();

    const newPhoneNumber = e.currentTarget.mobileNumber?.value || "";

    const isValidatePhone = await validatePhone(newPhoneNumber);
    if (!isValidatePhone) {
      return;
    }

    if (newPhoneNumber === currentPhone.substring(1)) {
      handleBack();
      return;
    }

    await handleSave(newPhoneNumber);
  };

  return (
    <div className="update-mobile-number-dialog">
      <Dialog
        data-testid="updateMobileNumberDialogId"
        title={
          <span>
            {intl.formatMessage(messages.updateMobileNumberDialogTitle)}
          </span>
        }
        onClose={handleBack}
      >
        <Form
          className="update-phone-form"
          noValidate
          onSubmit={handleSubmitForm}
          data-testid="updateMobileNumberFormId"
        >
          <PhoneField
            getValidationError={getValidationError}
            required={false}
          />
          <Button data-testid="submit" unelevated type="submit">
            {intl.formatMessage(messages.updateMobileNumberDialogSaveButton)}
          </Button>
          <Button outlined onClick={handleBack}>
            {intl.formatMessage(messages.updateMobileNumberDialogCancelButton)}
          </Button>
        </Form>
      </Dialog>
    </div>
  );
};

export default UpdateMobileNumberDialog;
